/* eslint-disable no-param-reassign */
import axios from 'axios';
import debounce from 'debounce-promise';
import { getCacheItem } from './cacheManager';
import handleAxiosError from './handleAxiosError';
import store from './store';
import getUrl from './getUrl';
import resetStale from './resetStale';

const cancelTokens = {};

const mutateDebounced = ({ debounceTime }) => debounce(({
  update, cache, props, options,
}) => {
  if (cancelTokens[cache.cacheKey]) {
    cancelTokens[cache.cacheKey].cancel('Operation canceled due to new mutation.');
  }

  const { CancelToken } = axios;
  cancelTokens[cache.cacheKey] = CancelToken.source();

  cache.mutations = {};
  cache.isMutating = false;

  return axios({
    method: cache.method,
    url: getUrl({ url: cache.url, params: cache.getParams({ props }) }),
    data: update,
    cancelToken: cancelTokens[cache.cacheKey].token,
  })
    .then((response) => {
      if (!cache.isMutating) {
        const { data } = response;
        const transformedData = cache.transform({ data, props });

        resetStale({ cache });

        store.dispatch({
          type: `${cache.cacheKey}:MUTATED`,
          payload: {
            data: transformedData,
            status: 'success',
            error: null,
            response: response.data,
          },
          options,
        });

        return { data: transformedData };
      }
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        console.log('Mutation request canceled', error.message);
      } else {
        handleAxiosError(error);

        if (error.response) {
          const { data } = error.response;

          store.dispatch({
            type: `${cache.cacheKey}:ERRORED`,
            payload: {
              error: data,
              response: error.response,
            },
          });
          return;
        }
        console.error(error);
      }
    });
}, debounceTime);

export default ({
  update, props, cacheKey, options,
}) => {
  const currentState = store.getState()[cacheKey];
  const currentCache = getCacheItem(cacheKey);
  const payload = update || currentState.data;

  let hasSentPromise = true;

  if (currentCache.isMutating) {
    currentCache.mutations = { ...currentCache.mutations, ...payload };
  } else {
    hasSentPromise = false;
    currentCache.mutateDebounce = mutateDebounced({
      debounceTime: currentCache.debounceTime,
    });
    currentCache.mutations = payload;
  }

  currentCache.isMutating = true;
  currentCache.isStale = false;

  if (currentState.status !== 'syncing') {
    store.dispatch({ type: `${cacheKey}:STATUS`, payload: 'syncing' });
  }

  const returnedPromise = currentCache.mutateDebounce({
    update: { ...currentCache.mutations },
    cache: currentCache,
    props,
    options,
  });

  if (!hasSentPromise) {
    return returnedPromise;
  }
};
